/**
 * Server page size, and the safety cap for reads without a date range. The
 * occurrence-aware contract (dashboard-api#664) enforces this maximum per
 * request; ranged reads paginate with the keyset cursor instead of relying on
 * a single capped page.
 */
export const MEDICATION_LOG_COMPATIBILITY_FETCH_LIMIT = 200;

/** The server rejects date ranges longer than this many calendar days. */
export const MEDICATION_LOG_SERVER_MAX_RANGE_DAYS = 31;

/**
 * Requested windows are widened by one day on each side before being sent to
 * the server. Stored rows mix true instants (takenAt) with scheduledFor
 * values that were historically written as patient-local naive datetimes, so
 * the server's instant boundaries can disagree with the client's patient-local
 * occurrence date by up to a timezone offset. The pad keeps every candidate
 * row in the response; `isMedicationLogInDateRange` then applies the exact
 * occurrence-date filter.
 */
export const MEDICATION_LOG_SERVER_WINDOW_PAD_DAYS = 1;

export type MedicationLogDateFields = {
  scheduledFor?: string | null;
  takenAt: string;
};

function getDefaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

type DateTimeParts = {
  date: string;
  time: string;
};

const ISO_LOCAL_DATE_TIME = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/;
const EXPLICIT_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;

function localPartsFromPrefix(value: string): DateTimeParts | null {
  const match = ISO_LOCAL_DATE_TIME.exec(value);
  if (!match) {
    const dateOnly = /^(\d{4}-\d{2}-\d{2})$/.exec(value);
    return dateOnly ? { date: dateOnly[1]!, time: "00:00" } : null;
  }

  return {
    date: match[1]!,
    time: `${match[2]}:${match[3]}`,
  };
}

function localPartsFromInstant(
  value: string,
  timezone: string,
): DateTimeParts | null {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(instant);

    const values = new Map(
      parts
        .filter((part) => part.type !== "literal")
        .map((part) => [part.type, part.value]),
    );
    const year = values.get("year");
    const month = values.get("month");
    const day = values.get("day");
    const hour = values.get("hour");
    const minute = values.get("minute");

    if (!year || !month || !day || !hour || !minute) return null;

    return {
      date: `${year}-${month}-${day}`,
      time: `${hour}:${minute}`,
    };
  } catch {
    // An invalid persisted timezone should not make a clinical history look
    // empty. Callers still get deterministic ISO-prefix compatibility below.
    return null;
  }
}

function localParts(value: string | null | undefined, timezone: string) {
  if (!value) return null;

  // scheduledFor is currently written as a patient-local naive datetime.
  // Preserve that local date/time instead of asking Date to reinterpret it in
  // the device timezone. Explicitly zoned values (including future server
  // responses) are converted to the patient's schedule timezone.
  if (!EXPLICIT_TIMEZONE.test(value)) {
    return localPartsFromPrefix(value);
  }

  return localPartsFromInstant(value, timezone) ?? localPartsFromPrefix(value);
}

/**
 * Serialize a patient-local wall-clock occurrence to the UTC instant the API
 * requires (#2357). POST /medications/logs validates scheduledFor with a
 * strict ISO datetime, so the naive `YYYY-MM-DDTHH:mm:00` convention used by
 * the scheduling surfaces is rejected with a 400. Values that already carry
 * an explicit timezone pass through untouched; naive values are interpreted
 * in the patient's schedule timezone and converted to a real instant, which
 * `localParts` maps back to the identical local date + minute on read.
 *
 * Returns null for values that cannot be parsed.
 */
export function serializeMedicationLogScheduledFor(
  value: string | null | undefined,
  timezone: string = getDefaultTimezone(),
): string | null {
  if (!value) return null;
  if (EXPLICIT_TIMEZONE.test(value)) return value;

  const parts = localPartsFromPrefix(value);
  if (!parts) return null;

  const [year, month, day] = parts.date.split("-").map(Number);
  const [hour, minute] = parts.time.split(":").map(Number);
  const secondsMatch = /T\d{2}:\d{2}:(\d{2})/.exec(value);
  const second = secondsMatch ? Number(secondsMatch[1]) : 0;
  const wallClockAsUtc = Date.UTC(
    year!,
    month! - 1,
    day!,
    hour!,
    minute!,
    second,
  );

  // Two-pass conversion: find the UTC instant whose wall clock in the target
  // timezone matches the requested local time. The second pass converges
  // across DST transitions; for skipped local times it lands on the instant
  // the zone actually observed.
  let instant = wallClockAsUtc;
  for (let i = 0; i < 2; i++) {
    const observed = localPartsFromInstant(new Date(instant).toISOString(), timezone);
    if (!observed) {
      // Unknown timezone: fall back to the device's interpretation of the
      // naive local time, which is still a valid instant for the API.
      const deviceLocal = new Date(
        `${parts.date}T${parts.time}:${String(second).padStart(2, "0")}`,
      );
      return Number.isNaN(deviceLocal.getTime())
        ? null
        : deviceLocal.toISOString();
    }
    const observedAsUtc = Date.UTC(
      Number(observed.date.slice(0, 4)),
      Number(observed.date.slice(5, 7)) - 1,
      Number(observed.date.slice(8, 10)),
      Number(observed.time.slice(0, 2)),
      Number(observed.time.slice(3, 5)),
      second,
    );
    instant += wallClockAsUtc - observedAsUtc;
    if (observedAsUtc === wallClockAsUtc) break;
  }

  return new Date(instant).toISOString();
}

/**
 * Return the local occurrence date for a medication log.
 *
 * scheduledFor is the occurrence identity. takenAt is used only for legacy
 * or genuinely unscheduled rows. Date ranges are inclusive on both ends.
 */
export function getMedicationLogOccurrenceDate(
  log: MedicationLogDateFields,
  timezone: string = getDefaultTimezone(),
): string | null {
  const scheduled = localParts(log.scheduledFor, timezone);
  if (scheduled) return scheduled.date;
  return localParts(log.takenAt, timezone)?.date ?? null;
}

/**
 * Normalize an occurrence timestamp to patient-local date + minute. This is
 * used when matching a rendered scheduled row to its administration log so a
 * late dose remains attached to the intended occurrence.
 */
export function getMedicationLogOccurrenceKey(
  value: string | null | undefined,
  timezone: string = getDefaultTimezone(),
): string | null {
  const parts = localParts(value, timezone);
  return parts ? `${parts.date}T${parts.time}` : null;
}

/**
 * Match a log to a rendered medication occurrence. Logs with an occurrence
 * identity must match that identity exactly; legacy logs fall back to their
 * patient-local takenAt date because they cannot distinguish same-medication
 * occurrences without scheduledFor.
 */
export function isMedicationLogForOccurrence(
  log: MedicationLogDateFields,
  scheduledFor: string | null | undefined,
  occurrenceDate: string,
  timezone?: string,
): boolean {
  const targetKey = getMedicationLogOccurrenceKey(scheduledFor, timezone);
  if (!targetKey) {
    return getMedicationLogOccurrenceDate(log, timezone) === occurrenceDate;
  }

  const logKey = getMedicationLogOccurrenceKey(log.scheduledFor, timezone);
  return logKey
    ? logKey === targetKey
    : getMedicationLogOccurrenceDate(log, timezone) === occurrenceDate;
}

export function isMedicationLogInDateRange(
  log: MedicationLogDateFields,
  options: {
    startDate?: string;
    endDate?: string;
    timezone?: string;
  },
): boolean {
  const date = getMedicationLogOccurrenceDate(log, options.timezone);
  if (!date) return false;
  if (options.startDate && date < options.startDate) return false;
  if (options.endDate && date > options.endDate) return false;
  return true;
}

export type MedicationLogServerWindow = {
  startDate: string;
  endDate: string;
};

function isoDateToUtcMs(date: string): number {
  const [year, month, day] = date.split("-").map(Number);
  return Date.UTC(year!, month! - 1, day!);
}

function utcMsToIsoDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Compute the server request windows for an inclusive patient-local date
 * range: the range is widened by `MEDICATION_LOG_SERVER_WINDOW_PAD_DAYS` on
 * each side (see that constant for why), then split into chunks the server
 * accepts (at most `MEDICATION_LOG_SERVER_MAX_RANGE_DAYS` calendar days
 * each). Returns null when either bound is missing or unparsable, or when the
 * range is inverted — callers fall back to the unranged capped fetch.
 */
export function getMedicationLogServerWindows(
  startDate: string | undefined,
  endDate: string | undefined,
): MedicationLogServerWindow[] | null {
  if (!startDate || !endDate) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(endDate)) return null;

  const rawStartMs = isoDateToUtcMs(startDate);
  const rawEndMs = isoDateToUtcMs(endDate);
  if (
    Number.isNaN(rawStartMs) ||
    Number.isNaN(rawEndMs) ||
    rawStartMs > rawEndMs
  ) {
    return null;
  }

  const startMs = rawStartMs - MEDICATION_LOG_SERVER_WINDOW_PAD_DAYS * DAY_MS;
  const endMs = rawEndMs + MEDICATION_LOG_SERVER_WINDOW_PAD_DAYS * DAY_MS;

  const windows: MedicationLogServerWindow[] = [];
  const chunkMs = MEDICATION_LOG_SERVER_MAX_RANGE_DAYS * DAY_MS;
  for (let chunkStart = startMs; chunkStart <= endMs; chunkStart += chunkMs) {
    const chunkEnd = Math.min(chunkStart + chunkMs - DAY_MS, endMs);
    windows.push({
      startDate: utcMsToIsoDate(chunkStart),
      endDate: utcMsToIsoDate(chunkEnd),
    });
  }
  return windows;
}
